import React from "react";
import { type Root, createRoot } from "react-dom/client";
import {
	applyEmbedHostIsolation,
	hoistTailwindPropertyRegistrations,
	prepareEmbedStyleText,
	readCapturedEmbedCss,
	syncEmbedFontFamily,
	syncEmbedHostHeight,
} from "./embed-host";
import { injectUseTriffinNavLink } from "./inject-use-triffin-nav";
import { PortalContainerProvider } from "./portal-container-context";
import stylesText from "./styles.css?inline";

// Extend Window interface for global API
declare global {
	interface Window {
		ViableWebflow?: Record<string, (element: HTMLElement, props?: unknown) => Root>;
	}
}

/**
 * Creates a bundle entry point for a Webflow component
 * @param componentName - The component name (e.g., "credit-calculator")
 * @param Component - The React component to render
 * @param componentDisplayName - The display name for the component (e.g., "CreditCalculator")
 */
export function createWebflowBundle<T extends Record<string, unknown>>(
	componentName: string,
	Component: React.ComponentType<T>,
	componentDisplayName: string,
) {
	// Initialize global API if it doesn't exist
	if (typeof window !== "undefined" && !window.ViableWebflow) {
		window.ViableWebflow = {};
	}

	// Auto-mount function
	function mountComponent(element: HTMLElement, props?: Partial<T>) {
		const disableShadow = element.dataset.viableShadow === "false";

		let portalContainer: Element | DocumentFragment = element;
		let mountNode: HTMLElement = element;

		applyEmbedHostIsolation(element);

		if (!disableShadow) {
			const shadowRoot = element.shadowRoot ?? element.attachShadow({ mode: "open" });
			portalContainer = shadowRoot;

			// Inject component styles into the shadow root once. Vite CSS is captured
			// onto window (not document.head) so Webflow reveal layers stay single-paint.
			const styleKey = "viable-webflow-styles";
			if (!shadowRoot.querySelector(`style[data-${styleKey}]`)) {
				const styleText = prepareEmbedStyleText(stylesText, readCapturedEmbedCss());
				hoistTailwindPropertyRegistrations(styleText);
				const styleEl = document.createElement("style");
				styleEl.setAttribute(`data-${styleKey}`, "true");
				styleEl.textContent = styleText;
				shadowRoot.appendChild(styleEl);
			}

			syncEmbedFontFamily(element);

			// Create or reuse mount node inside shadow root
			const id = "viable-react-root";
			const existingMount = shadowRoot.getElementById?.(id) as HTMLElement | null;
			if (existingMount) {
				mountNode = existingMount;
			} else {
				const div = document.createElement("div");
				div.id = id;
				shadowRoot.appendChild(div);
				mountNode = div;
			}

			syncEmbedHostHeight(element, mountNode);
		}

		const root = createRoot(mountNode);
		root.render(
			<React.StrictMode>
				<PortalContainerProvider container={portalContainer}>
					<Component {...(props as T)} />
				</PortalContainerProvider>
			</React.StrictMode>,
		);
		return root;
	}

	// Expose global API
	const mountFunctionName = `mount${componentDisplayName}` as keyof typeof window.ViableWebflow;
	if (typeof window !== "undefined" && window.ViableWebflow) {
		window.ViableWebflow[mountFunctionName] = mountComponent as (element: HTMLElement, props?: unknown) => Root;
	}

	// Auto-mount on DOM ready
	function autoMount() {
		// Try to mount to component ID (Webflow mode)
		const element = document.getElementById(componentName);
		injectUseTriffinNavLink(element?.getAttribute("data-props"));
		if (element && element.dataset.viableMounted !== "true") {
			// Parse props from the host element's data-props (landing-page CMS hrefs stay here, not as React props).
			let props: Partial<T> | undefined;
			const propsAttr = element.getAttribute("data-props");
			if (propsAttr) {
				try {
					props = JSON.parse(propsAttr) as Partial<T>;
				} catch (e) {
					console.warn(`Failed to parse data-props for ${componentName}:`, e);
				}
			}

			// Mount the component
			mountComponent(element, props);
			element.dataset.viableMounted = "true";
			return;
		}
	}

	// Run auto-mount when DOM is ready
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", autoMount);
	} else {
		autoMount();
	}

	// Also run on dynamic content (for SPA scenarios)
	if (typeof window !== "undefined") {
		// Use MutationObserver to watch for new elements
		const observer = new MutationObserver(() => {
			autoMount();
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});
	}
}
